import React, { useState, useCallback, useEffect } from 'react';
import { Box, Grid } from '@material-ui/core';
import find from 'lodash/find';
import { Switch } from '@DesignSystem/controllers';
import useOrganizationSettings from '@shared/api/useOrganizationSettings';
import useWebhookSecrets from '@shared/api/useWebhookSecrets';
import useUpdateWebhookSecrets from '@shared/api/useUpdateWebhookSecrets';
import useOrganizationSettingsUpdateMutation from '@account-settings/api/useOrganizationSettingsUpdateMutation';
import { ORGANIZATIONS_SETTINGS } from '@shared/constants/organization';
import { Snackbar } from '@DesignSystem/feedback';
import SmallLoader from '@shared/components/SmallLoader';
import { Button } from '@ds';
import {
  BETA_FEATURE_MODEL_PRODUCTION_MONITORING_INTERNAL,
  BETA_FEATURE_MODEL_PRODUCTION_MONITORING_SLACK_CONNECTION
} from '@/lib/betaFeatures';
import useErrorMessageParam from '@/helpers/custom-hooks/useErrorMessageParam';
import classNames from './CustomizeOrganizationSettings.module.scss';
import { useCurrentOrganization } from '@shared/hooks';
import useBetaFeatureEnabled from '@shared/hooks/useBetaFeatureEnabled';
import { useSlackConnectionStatus } from '@shared/api/useSlackConnectionStatus';
import { useSlackConnectionDisconnect } from '@shared/api/useSlackConnectionDisconnect';
import { IS_ON_PREMISE } from '@/constants/configConstants';
import { SLACK_STATUS } from '@shared/constants';
import { BasicModal } from '@DesignSystem/modals';
import { KeyValueList, STARS_VALUE } from './KeyValueList';

const CustomizeOrganizationSettings = () => {
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [secretKeys, setSecretKeys] = useState([]);
  const [initialKeyValues, setInitialKeyValues] = useState([]);
  const currentOrganization = useCurrentOrganization();
  const disconnectSlackMutation = useSlackConnectionDisconnect();
  useErrorMessageParam();

  const { data: orgSettings, isLoading } = useOrganizationSettings({
    organizationId: currentOrganization?.id
  });

  const { data: webhookSecretNames } = useWebhookSecrets(
    currentOrganization?.id
  );

  const onlyAdminInviteFromOutsideOrg = find(
    orgSettings,
    ({ name }) =>
      name ===
      ORGANIZATIONS_SETTINGS.ONLY_ADMIN_INVITE_FROM_OUTSIDE_ORGANIZATION
  )?.value;

  const fullMetricsEnabled = find(
    orgSettings,
    ({ name }) => name === ORGANIZATIONS_SETTINGS.FULL_METRICS_ENABLED
  )?.value;

  const { data: connectedToSlack } = useSlackConnectionStatus(
    {
      organizationId: currentOrganization?.id
    },
    { refetchOnWindowFocus: true }
  );

  useEffect(() => {
    if (currentOrganization?.id) {
      localStorage.setItem('currentOrganizationId', currentOrganization?.id);
    }
  }, [currentOrganization?.id]);

  const isSlackConnected = connectedToSlack === SLACK_STATUS.CONNECTED;

  const updateWorkspaceMutation = useOrganizationSettingsUpdateMutation(
    currentOrganization?.id,
    ORGANIZATIONS_SETTINGS.ONLY_ADMIN_INVITE_FROM_OUTSIDE_ORGANIZATION
  );

  const updateFullMetricsEnabled = useOrganizationSettingsUpdateMutation(
    currentOrganization?.id,
    ORGANIZATIONS_SETTINGS.FULL_METRICS_ENABLED
  );

  const {
    mutate: updateWebhookSecrets,
    isLoading: isSecretsUpdateLoading
  } = useUpdateWebhookSecrets(currentOrganization?.id);

  const isSubmitSecretsDisabled =
    secretKeys.length === 0 ||
    isSecretsUpdateLoading ||
    new Set(secretKeys.map(s => s.secretName)).size !== secretKeys.length ||
    secretKeys.some(
      ({ secretName, secretValue }) =>
        (secretName && !secretValue) || (!secretName && secretValue)
    );

  const isMPMEnabled = useBetaFeatureEnabled(
    BETA_FEATURE_MODEL_PRODUCTION_MONITORING_INTERNAL
  );
  const isSlackConnectionEnabled = useBetaFeatureEnabled(
    BETA_FEATURE_MODEL_PRODUCTION_MONITORING_SLACK_CONNECTION
  );

  const hideOnlyAdminInviteFromOutsideOrganization = IS_ON_PREMISE;

  const changeOrganizationSettingsForWorkspace = useCallback(() => {
    updateWorkspaceMutation.mutate(!onlyAdminInviteFromOutsideOrg);
  }, [onlyAdminInviteFromOutsideOrg, updateWorkspaceMutation]);

  const changeFullMetricsEnabled = useCallback(() => {
    updateFullMetricsEnabled.mutate(!fullMetricsEnabled);
  }, [fullMetricsEnabled, updateFullMetricsEnabled]);

  const changeWebhookSecretKeys = useCallback(() => {
    updateWebhookSecrets(
      {
        secrets: getUpdatedWebhookSecrets(initialKeyValues, secretKeys)
      },
      {
        onSuccess: () => {
          setIsModalOpened(false);
        }
      }
    );
  }, [secretKeys, initialKeyValues, updateWebhookSecrets]);

  const isSaving = updateWorkspaceMutation.status === 'loading';

  const disconnectHandler = () => {
    disconnectSlackMutation.mutate(currentOrganization?.id);
  };

  if (isLoading) {
    return <SmallLoader />;
  }

  return (
    <div className={classNames.organizationSettingsContainer}>
      <Snackbar open={isSaving} message="Saving..." />

      <Box component="h4" className="page-title" mb={3}>
        Organization settings
      </Box>

      <Box className="field-label" mb={1.5}>
        Customize your organization settings
      </Box>

      {!hideOnlyAdminInviteFromOutsideOrganization && (
        <Grid
          container
          alignItems="flex-start"
          alignContent="flex-start"
          justifyContent="flex-start"
        >
          <Grid item xs={1}>
            <Switch
              disabled={isSaving}
              checked={onlyAdminInviteFromOutsideOrg}
              onChange={changeOrganizationSettingsForWorkspace}
              color="primary"
            />
          </Grid>

          <Grid item xs={11}>
            <label
              onClick={changeOrganizationSettingsForWorkspace}
              className={classNames.organizationSettingsToggleLabel}
            >
              Only organization admins can invite users into the organization
            </label>

            <small className={classNames.organizationSettingsToggleText}>
              When disabled, all members in the organization can invite users
              into the organization which can influence billing. When enabled,
              only organization admins will be able to invite users to the
              organization.
            </small>
          </Grid>
        </Grid>
      )}
      <Grid
        container
        alignItems="flex-start"
        alignContent="flex-start"
        justifyContent="flex-start"
        style={{ marginTop: '20px' }}
      >
        <Grid item xs={1}>
          <Switch
            disabled={isSaving}
            checked={fullMetricsEnabled}
            onChange={changeFullMetricsEnabled}
            color="primary"
          />
        </Grid>
        <Grid item xs={11}>
          <label
            onClick={changeFullMetricsEnabled}
            className={classNames.organizationSettingsToggleLabel}
          >
            Store metrics without sampling
          </label>

          <small className={classNames.organizationSettingsToggleText}>
            Enable this setting to retain all logged values for metrics to
            inspect your data with more detail, such as when investigating
            outliers. The data will be stored as assets and can be visualized
            with Python panels.{' '}
            <a
              href="/docs/v2/guides/experiment-management/limits-and-performance/#metric-sampling"
              target="_blank"
              rel="noreferrer"
            >
              Read more in documentation.
            </a>
          </small>
        </Grid>
      </Grid>
      {isMPMEnabled && isSlackConnectionEnabled && (
        <div>
          <Box className="field-label" mt={5}>
            Model Monitoring Slack Integration
          </Box>
          <Box mt={1.5}>
            {!isSlackConnected && (
              <small className={classNames.organizationSettingsToggleText}>
                Connect to Slack workspace to receive MPM alerts in Slack. Once
                configured you will be able to select the Slack channel to send
                the alerts to in the MPM alerts configuration screen.
              </small>
            )}
            {isSlackConnected && (
              <>
                <small className={classNames.organizationSettingsToggleText}>
                  The integration is already configured.
                </small>
                <span className={classNames.disconnectButton}>
                  <Button
                    onClick={disconnectHandler}
                    size="medium"
                    style={{ backgroundColor: '#19A979' }}
                  >
                    Disconnect
                  </Button>
                </span>
              </>
            )}
          </Box>
          {!isSlackConnected && (
            <Box mt={2}>
              <a
                href={`/api/slack/oauth/start?organizationId=${currentOrganization?.id}`}
                target="_blank"
                rel="noreferrer"
              >
                <Button size="medium" text="Connect">
                  Connect
                </Button>
              </a>
            </Box>
          )}
        </div>
      )}

      <div>
        <Box className="field-label" mt={5}>
          Webhook secrets
        </Box>
        <Box mt={1.5}>
          <small className={classNames.organizationSettingsToggleText}>
            Configure organization-level secrets to be used when creating model
            registry webhooks.
          </small>
        </Box>
        <Box mt={2}>
          <Button
            size="medium"
            text="Connect"
            onClick={() => {
              setIsModalOpened(true);
            }}
          >
            Manage secrets ({webhookSecretNames?.secrets?.length || 0})
          </Button>
        </Box>
        <BasicModal
          additionalClasses={{
            paper: classNames.manageSecretsPopoverPaper
          }}
          open={isModalOpened}
          onClose={() => {
            setIsModalOpened(false);
          }}
          title="Manage Secrets"
          content={
            <div className={classNames.secretsModalContentWrapper}>
              <KeyValueList
                keyValueFields={secretKeys}
                baseNames={webhookSecretNames?.secrets}
                onFieldsChange={setSecretKeys}
                initialKeyValues={initialKeyValues}
                handleInitialKeyValues={setInitialKeyValues}
              />
            </div>
          }
          footerActions={[
            {
              onClick: () => {
                setIsModalOpened(false);
              },
              disabled: false,
              key: 'modal-cancel',
              text: 'Cancel',
              type: 'tertiary'
            },
            {
              onClick: changeWebhookSecretKeys,
              disabled: isSubmitSecretsDisabled,
              key: 'changes-save',
              text: 'Save',
              type: 'primary'
            }
          ]}
        />
      </div>
    </div>
  );
};

export default CustomizeOrganizationSettings;

function getUpdatedWebhookSecrets(initialKeyValues, updatedKeyValues) {
  const nonEmptySecrets = updatedKeyValues.filter(
    keyValue => keyValue.secretName !== '' && keyValue.secretValue !== ''
  );

  const updatedIds = new Set(nonEmptySecrets.map(item => item.id));
  const updatedKeys = new Set(nonEmptySecrets.map(item => item.secretName));

  const deletedSecrets = initialKeyValues
    .filter(
      item => !updatedIds.has(item.id) && !updatedKeys.has(item.secretName)
    )
    .map(item => ({ secretName: item.secretName, secretValue: null }));

  const modifiedSecrets = nonEmptySecrets
    .filter(item => item.secretValue !== STARS_VALUE)
    .map(item => ({
      secretName: item.secretName,
      secretValue: item.secretValue
    }));

  return [...deletedSecrets, ...modifiedSecrets];
}
